import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useAuthorizationsList, useDecideAuthorization } from '../hooks/useAuthorizationsApi';
import { useToast } from '../components/ui/Toast';
import { NameWithCode, HubspotChip, BcLink } from '../components/ui/EntityLinks';
import { LoadError } from '../components/ui/LoadError';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Field';
import { FOCUS_RING } from '../components/ui/focus';
import { SkeletonCards, LoadingAnnouncement } from '../components/ui/Skeleton';
import { isoDate, isoDateTime } from '../lib/dates';
// The four reasons used to live here as `REASON_LABEL`, the only label map in
// the whole SPA. P22 absorbed it into `lib/labels.ts` so this page and the
// ticket screen cannot describe the same state differently.
import { AUTHORIZATION_REASON_LABELS, TICKET_STATUS_LABELS, labelFor, tooltipFor } from '../lib/labels';

/**
 * The queue is Pending; the other four states are history the page could not
 * reach at all until 2026-08-28.
 *
 * Bruno, working the pilot: *"I do not see DRA-2026-00001 in Authorization
 * maybe because -> NeedsReview — AuthorizationRequestExpired"*. He was right
 * about the cause. `authorizations/list.ts` has taken `?status=` since P7 and
 * this page never sent one, so the five requests the expiry timer had already
 * moved to `Expired` were invisible — including the tickets whose `Tickets`
 * row still reads `AuthorizationStatus='AwaitingSalesApproval'`, which is
 * exactly the pair a person needs to see together to understand what happened.
 *
 * `Expired` sits second on purpose: it is the one a Sales Manager or
 * Regulatory has to go looking for. Nothing decided here changes — an expired
 * request has no buttons, because the ladder already handed the ticket on.
 */
const STATUS_VIEWS = [
  { key: 'Pending', label: 'Pending', empty: 'Nothing is waiting on a Sales decision.' },
  { key: 'Expired', label: 'Expired', empty: 'No request has run out of time.' },
  { key: 'Approved', label: 'Approved', empty: 'Nothing approved yet.' },
  { key: 'Rejected', label: 'Rejected', empty: 'Nothing rejected yet.' },
] as const;


/**
 * The one page a Sales-role user can edit (F-04). Every Pending row here
 * names a real recipient — see agent/steps/authorize.ts: a request row is
 * proof somebody was asked, never a dangling wait.
 */
export default function Authorizations() {
  const { canEdit } = usePagePermissions('/authorizations');
  const { toast } = useToast();
  const [status, setStatus] = useState<(typeof STATUS_VIEWS)[number]['key']>('Pending');
  const activeView = STATUS_VIEWS.find(v => v.key === status) ?? STATUS_VIEWS[0];
  const { data: requests, isLoading, isError, error, errorUpdatedAt, refetch } = useAuthorizationsList(status);
  const [commentById, setCommentById] = useState<Record<number, string>>({});
  const decide = useDecideAuthorization();

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Sales Authorizations</h1>
          <p className="mt-1 text-sm text-gray-500">
            A customer requested a document but hasn't bought this product recently — the responsible Sales Manager decides.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-gray-200 bg-white p-0.5">
          {STATUS_VIEWS.map(v => (
            <button
              key={v.key}
              type="button"
              onClick={() => setStatus(v.key)}
              aria-pressed={v.key === status}
              // P40: §7's filter-chip rule - applied = an ink ground - on tokens, plus
              // the house ring, which this segmented control never had. Same shape as
              // P39's CompanyScopeButton and deliberately not the Button kit: a chip
              // that reports a selection is not a button that performs an action.
              className={`h-8 rounded-ctl px-2.5 text-xs font-medium ${FOCUS_RING} ${
                v.key === status ? 'bg-ink text-surface' : 'text-body hover:bg-hover'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {isError && <LoadError what="Authorization requests" error={error} at={errorUpdatedAt} onRetry={refetch} />}
      {isLoading && (
        <>
          <LoadingAnnouncement what="authorization requests" />
          {/* Cards, not rows: the skeleton is the shape of what is coming, and this
              page is a list of cards. A table skeleton here would be a promise the
              page does not keep. */}
          <SkeletonCards count={3} />
        </>
      )}

      {!isLoading && !isError && (requests ?? []).length === 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">{activeView.empty}</div>
      )}

      {!isLoading && !isError && (
        <div className="mt-4 space-y-3">
          {(requests ?? []).map(r => (
            <div key={r.id} className="rounded-lg border border-gray-200 bg-white p-4 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <Link to={`/tickets/${r.ticketNumber}`} className="font-medium text-info hover:underline">{r.ticketNumber}</Link>
                <span className="text-xs text-gray-500">
                  requested {isoDate(r.requestedAt)}
                  {r.dueAt ? ` · due ${isoDate(r.dueAt)}` : ''}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 text-gray-600 sm:grid-cols-3">
                <div>
                  <span className="text-muted">Customer</span>{' '}
                  <span className="inline-flex items-center gap-1.5">
                    <NameWithCode name={r.customerName} code={r.customerCode} raw={r.rawCustomerText ?? r.customerId} />
                    <BcLink url={r.bcCustomerUrl} />
                    <HubspotChip url={r.hubspotUrl} />
                  </span>
                </div>
                <div>
                  <span className="text-muted">Product</span>{' '}
                  <span className="inline-flex items-center gap-1.5">
                    <NameWithCode name={r.productName} code={r.productCode} raw={r.rawProductText ?? r.productId} />
                    <BcLink url={r.bcProductUrl} />
                  </span>
                </div>
                <div><span className="text-muted">Document</span> {r.requestedDocTypeCode}</div>
              </div>
              <div className="mt-2 text-gray-700" title={tooltipFor(AUTHORIZATION_REASON_LABELS, r.reason)}>
                {labelFor(AUTHORIZATION_REASON_LABELS, r.reason)}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Asking: {r.salesManagerName ?? r.requestedFrom ?? '—'}
                {r.salesManagerName && r.requestedFrom ? ` (${r.requestedFrom})` : ''}
              </div>
              {r.ticketStatus === 'NeedsReview' && (
                // Amber survives here and is correct: `/authorizations` is the screen
                // where this ticket's OTHER open question is somebody's to answer, and
                // §8 assigns the tint to exactly that. Re-pointed to `--warn-ink`
                // (5.8:1) from `amber-700` (4.1:1).
                <div className="mt-1 text-xs text-warn-ink">
                  This ticket is also in Regulatory&apos;s <span title={tooltipFor(TICKET_STATUS_LABELS, 'NeedsReview')}>review queue</span> for
                  a separate reason — deciding this doesn&apos;t move it out of there.
                </div>
              )}

              {r.status === 'Pending' && canEdit && (
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                  {/* P40: the kit's controls. The Approve button was a filled
                      `bg-green-600` - stock green, not `--brand-600`, so the one page
                      where a Sales Manager makes a real decision was the one page whose
                      primary was not the house action colour (audit S2-03). Reject was
                      already an outline and is now the kit's `destructive`, which is the
                      same shape with the measured `--err` values. The comment box gets a
                      focus ring and an accessible name it did not have. */}
                  <Input
                    aria-label="Comment on this decision (optional)"
                    placeholder="Comment (optional)"
                    value={commentById[r.id] ?? ''}
                    onChange={e => setCommentById(c => ({ ...c, [r.id]: e.target.value }))}
                    className="min-w-[200px] flex-1"
                  />
                  <Button
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: r.id, decision: 'Approved', comment: commentById[r.id] }, {
                      onSuccess: () => toast('Authorized', 'success'),
                      onError: () => toast('Failed to decide — it may already have been decided', 'error'),
                    })}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: r.id, decision: 'Rejected', comment: commentById[r.id] }, {
                      onSuccess: () => toast('Rejected', 'success'),
                      onError: () => toast('Failed to decide — it may already have been decided', 'error'),
                    })}
                  >
                    Reject
                  </Button>
                </div>
              )}
              {r.status !== 'Pending' && (
                <div className="mt-2 text-xs text-gray-500">
                  {r.status} by {r.decidedBy ?? '—'} {r.decidedAt ? isoDateTime(r.decidedAt) : ''}
                  {r.comment ? ` — ${r.comment}` : ''}
                </div>
              )}
            </div>
          ))}
          {(requests ?? []).length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-muted">
              Nothing waiting on a decision.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
